// useThemeColor.js
// Pins the phone status-bar tint (the single <meta name="theme-color"
// id="theme-color-meta">) to a fixed color while a component is mounted — for
// the always-dark brand surfaces (landing page, cold-start / welcome splash) so
// the status bar MATCHES the page instead of the app's stored light/dark mode.
//
// Why a shared module (not just a setAttribute in the hook): React runs child
// effects BEFORE parent effects, so App.js's theme effect (parent) would re-set
// the meta to the app background AFTER the landing (child) pinned it dark. So
// both go through applyStatusBarColor(), which honors the forced stack first —
// App.js calls it on every theme change, the hook pushes/pops the stack.
import { useEffect } from "react";

// Stack of forced colors from mounted brand surfaces; the top of the stack wins,
// so a splash stacked over the landing restores correctly when it dismisses.
const stack = [];

export function applyStatusBarColor() {
  const forced = stack[stack.length - 1];
  // The status bar follows the app theme — set to the current --bg-1 so it
  // matches the app background (light bar in light mode, dark in dark). NOTE:
  // on some Android skins (One UI/MIUI/etc.) a LIGHT status bar reintroduces a
  // faint hairline the OS draws under it for icon contrast — visible only in
  // light mode and unremovable from web/CSS (see pwa-status-bar-light-line).
  // Accepted as a deliberate tradeoff: a light bar in light mode is preferred
  // over pinning the bar dark (a "dark cap"). Brand surfaces still push their
  // own color via the forced stack.
  const desired =
    forced || getComputedStyle(document.documentElement).getPropertyValue("--bg-1").trim();
  if (!desired) return;

  const existing = document.getElementById("theme-color-meta");
  if (existing && existing.getAttribute("content") === desired) return; // no-op, no repaint

  // REPLACE the meta node rather than mutating its content: Chrome mobile leaves
  // a 1px seam under the status bar when theme-color is changed in place at
  // runtime (it doesn't cleanly repaint the top). Swapping the element forces a
  // fresh read + clean repaint, so switching to a light bar no longer leaves a
  // hairline that persists until reload.
  // Replace the node (not just set content): a fresh element is the most
  // reliable way for the browser to re-read theme-color.
  const meta = document.createElement("meta");
  meta.setAttribute("name", "theme-color");
  meta.setAttribute("id", "theme-color-meta");
  meta.setAttribute("content", desired);
  if (existing) existing.replaceWith(meta);
  else document.head.appendChild(meta);
}

export function useThemeColor(color) {
  useEffect(() => {
    if (!color) return undefined;
    stack.push(color);
    applyStatusBarColor();
    return () => {
      const i = stack.lastIndexOf(color);
      if (i !== -1) stack.splice(i, 1);
      applyStatusBarColor();
    };
  }, [color]);
}

export default useThemeColor;
